// src/stores/useExpenseStore.js
import { defineStore } from 'pinia';
import global from '@/global';
import { useAuthStore } from '@/stores/auth';
import { toCents, fromCents } from '@/composables/numberUtils';

export const useExpenseStore = defineStore('expenseStore', {
  state: () => ({
    totalExpenses: 0,
    expenses: {
      personnel_and_benefits: [],
      professional_and_contractual_fees: [],
      printing_and_publications: [],
      travel_conferences_and_meetings: [],
      marketing_and_communications: [],
      facilities: [],
      other_expenses: [],
      in_kind: [],
      programs: []
    },
    loading: false,
    error: null
  }),

  actions: {
    setTotalExpenses(expenses) {
      this.totalExpenses = expenses;
    },

    async fetchExpenses(params = {}) {
      try {
        this.loading = true;
        const authStore = useAuthStore();
        
        const queryParams = new URLSearchParams();
        if (params.program_id) queryParams.append('program_id', params.program_id);
        if (params.project_id) queryParams.append('project_id', params.project_id);
        
        const response = await fetch(`${global.API_URL}/expenses?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch expenses');
        }

        const data = await response.json();
        
        // Ensure all expense types exist in the data
        Object.keys(this.expenses).forEach(type => {
          if (!data.expenses[type]) {
            data.expenses[type] = [];
          }
        });

        this.expenses = data.expenses;
        
        // Calculate total expenses using integer cents to avoid floating point precision issues
        let totalCents = 0;
        Object.values(data.expenses).forEach(expenseGroup => {
          if (Array.isArray(expenseGroup)) {
            expenseGroup.forEach(expense => {
              if (!expense) return;
              const cost = (typeof expense.expense_cost === 'string')
                ? parseFloat(expense.expense_cost.replace(/,/g, '') || 0)
                : (expense.expense_cost || 0);
              totalCents += Math.round(Number(cost) * 100);
            });
          }
        });
        this.totalExpenses = fromCents(totalCents);

        return data;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    }
  },

  getters: {
    getTotalExpenses: (state) => state.totalExpenses,
    getExpenses: (state) => state.expenses,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    
    // Get expenses by type
    getExpensesByType: (state) => (type) => {
      return state.expenses[type] || [];
    },
    
    // Get all expense types
    getExpenseTypes: (state) => {
      return Object.keys(state.expenses).filter(type => 
        state.expenses[type] && 
        Array.isArray(state.expenses[type]) && 
        state.expenses[type].length > 0
      );
    },

    // Get total by expense type
    getTotalByType: (state) => (type) => {
      if (!state.expenses[type] || !Array.isArray(state.expenses[type])) {
        return 0;
      }
      const cents = state.expenses[type].reduce((acc, expense) => {
        if (!expense) return acc;
        const cost = (typeof expense.expense_cost === 'string')
          ? parseFloat(expense.expense_cost.replace(/,/g, '') || 0)
          : (expense.expense_cost || 0);
        return acc + Math.round(Number(cost) * 100);
      }, 0);
      return fromCents(cents);
    }
  }
});
